const stripAccents = (value = '') =>
  String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const cleanToken = (value = '') => stripAccents(value).replace(/[^a-z0-9]+/g, ' ').trim();

const WEAPON_PATTERNS = [
  { value: 'akm', label: 'AKM', pattern: /\bakm\b/ },
  { value: 'akm', label: 'AKM', pattern: /\bak\b/ },
  { value: 'm416', label: 'M416', pattern: /\bm416\b|\bm4\b/ },
  { value: 'm762', label: 'Beryl M762', pattern: /\bm762\b|\bberyl\b|\bberril\b|\bbrryl\b|\bberyl\s*m762\b/ },
  { value: 'groza', label: 'Groza', pattern: /\bgroza\b/ }
];

const SCOPE_PATTERNS = [
  { value: 'red_dot', label: 'Red Dot', pattern: /\bred\s*dot\b|\breddot\b|\bponto\s*vermelho\b/ },
  { value: 'holo', label: 'Holo', pattern: /\bholo\b|\bholografica\b|\bholografico\b/ },
  { value: '2x', label: '2x', pattern: /\b2x\b|\bx2\b/ },
  { value: '3x', label: '3x', pattern: /\b3x\b|\bx3\b/ },
  { value: '4x', label: '4x', pattern: /\b4x\b|\bx4\b|\bacog\b/ },
  { value: '6x', label: '6x', pattern: /\b6x\b|\bx6\b/ },
  { value: '8x', label: '8x', pattern: /\b8x\b|\bx8\b/ }
];

const SENSITIVITY_PATTERNS = [
  { value: 'ADS Gyro', controlMode: 'gyro', pattern: /\bads\s*gyro\b|\bgyro\s*ads\b|\bads\s*giroscopio\b|\bgiroscopio\s*ads\b/ },
  { value: 'ADS', controlMode: 'camera', pattern: /\bads\b/ },
  { value: 'Gyro', controlMode: 'gyro', pattern: /\bgyro\b|\bgiroscopio\b/ },
  { value: 'Camera', controlMode: 'camera', pattern: /\bcamera\b|\bcamera\b/ }
];

const CODM_PATTERN = /\bcodm\b|\bcod\s*mobile\b|\bcall\s*of\s*duty(?::?\s*mobile)?\b/;
const FREE_FIRE_PATTERN = /\bfree\s*fire\b|\bfreefire\b|\bff\b/;
const PUBG_PATTERN = /\bpubg\b|\bpubg\s*mobile\b|\bpubgm\b/;
const CALCULATOR_PATTERN = /\bcalcul\w*\b|\bcalculadora\b/;
const NATURAL_CALCULATOR_INTENT_PATTERN = new RegExp([
  '\\bachar\\s+(os\\s+)?valores\\b',
  '\\bencontrar\\s+(os\\s+)?valores\\b',
  '\\bvalores?\\s+(para|pras?)\\s+as\\s+outras\\b',
  '\\boutras\\s+miras\\b',
  '\\bquero\\s+(as\\s+)?outras\\b',
  '\\bpreciso\\s+(das\\s+)?outras\\b',
  '\\bbase\\s+(para|pras?)\\s+as\\s+outras\\b',
  '\\bmira\\s+(esta|ta|e)?\\s*perfeita\\b',
  '\\bsensi\\s+(esta|ta|e)?\\s*perfeita\\b',
  '\\bperfeita\\b.*\\boutras\\b'
].join('|'));
const REFERENCE_LANGUAGE_PATTERN = /\b(minha|meu|da|do|na|no|usando|usar|base|mira|sensi|perfeita)\b/;
const SYMPTOM_WITHOUT_CALC_PATTERN = /\b(sobe|subindo|treme|tremendo|abre|abrindo|ruim|errad[ao]|erro|recoil|spray|passa|puxa)\b/;

const hasCalculatorIntent = (normalized = '') => (
  CALCULATOR_PATTERN.test(normalized) || NATURAL_CALCULATOR_INTENT_PATTERN.test(normalized)
);

export const normalizePubgCalculatorSlotAliases = (slots = {}) => {
  const normalized = { ...slots };

  if (normalized.mainScope && !normalized.baseScope) normalized.baseScope = normalized.mainScope;
  if (normalized.knownPerfectValue !== undefined && normalized.baseValue === undefined) {
    normalized.baseValue = normalized.knownPerfectValue;
  }
  if (normalized.controlMode && !normalized.sensitivityType) {
    normalized.sensitivityType = normalized.controlMode;
  }

  const normalizedGame = cleanToken(normalized.game);
  if (normalizedGame) {
    if (CODM_PATTERN.test(normalizedGame)) normalized.game = 'codm';
    else if (FREE_FIRE_PATTERN.test(normalizedGame)) normalized.game = 'free_fire';
    else if (PUBG_PATTERN.test(normalizedGame) || normalizedGame === 'pubg mobile') normalized.game = 'pubg_mobile';
  }

  const normalizedWeapon = cleanToken(normalized.weapon);
  if (normalizedWeapon) {
    const weapon = extractWeapon(normalizedWeapon);
    if (weapon) {
      normalized.weapon = weapon.value;
      if (!normalized.weaponLabel) normalized.weaponLabel = weapon.label;
    }
  }

  const normalizedScope = cleanToken(normalized.baseScope);
  if (normalizedScope) {
    const scope = extractScope(normalizedScope);
    if (scope) {
      normalized.baseScope = scope.value;
      if (!normalized.baseScopeLabel) normalized.baseScopeLabel = scope.label;
    }
  }

  const normalizedType = cleanToken(normalized.sensitivityType);
  if (normalizedType) {
    const sensitivityType = extractSensitivityType(normalizedType);
    if (sensitivityType) {
      normalized.sensitivityType = sensitivityType.value;
      if (!normalized.controlMode) normalized.controlMode = sensitivityType.controlMode;
    }
  }

  return normalized;
};

export const isCodmCalculatorRequest = (text = '') => {
  const normalized = cleanToken(text);
  return hasCalculatorIntent(normalized) && CODM_PATTERN.test(normalized);
};

export const shouldHandlePubgCalculatorSkillText = (text = '') => {
  // Fallback legacy heuristic only. The main Mi routing must come from the
  // semantic intent resolver, not from this parser.
  const normalized = cleanToken(text);
  if (!normalized) return false;
  if (isCodmCalculatorRequest(normalized)) return true;
  if (hasCalculatorIntent(normalized) && FREE_FIRE_PATTERN.test(normalized)) return false;

  const weapon = extractWeapon(normalized);
  const scope = extractScope(normalized);
  const value = extractBaseValue(normalized);
  const type = extractSensitivityType(normalized);
  const invalidScope = extractInvalidScope(normalized, scope);
  const hasIntent = hasCalculatorIntent(normalized);
  const hasReferenceLanguage = REFERENCE_LANGUAGE_PATTERN.test(normalized);
  const hasSymptomOnly = SYMPTOM_WITHOUT_CALC_PATTERN.test(normalized) && !hasIntent;

  if (hasSymptomOnly) return false;
  if (hasIntent && (weapon || scope || value !== null || type || invalidScope)) return true;
  if (weapon && (scope || invalidScope) && hasReferenceLanguage) return true;
  if (weapon && /\bmira\b/.test(normalized)) return true;
  return Boolean(weapon && scope && value !== null && type);
};

const extractWeapon = (normalized) => (
  WEAPON_PATTERNS.find((entry) => entry.pattern.test(normalized)) || null
);

const extractScope = (normalized) => (
  SCOPE_PATTERNS.find((entry) => entry.pattern.test(normalized)) || null
);

const extractInvalidScope = (normalized, scope) => {
  if (scope) return null;
  const match = normalized.match(/\b(\d{1,2}x)\b/);
  if (!match) return null;
  return {
    slot: 'baseScope',
    value: match[1],
    reason: 'unsupported_scope'
  };
};

const extractBaseValue = (normalized) => {
  const withoutMixedTokens = normalized
    .replace(/\bm\d+\b/g, ' ')
    .replace(/\b\d+x\b/g, ' ')
    .replace(/\b\d+v\d+\b/g, ' ');
  const match = withoutMixedTokens.match(/\b(\d{1,3}(?:[.,]\d{1,2})?)\b/);
  if (!match) return null;

  const value = Number(match[1].replace(',', '.'));
  return Number.isFinite(value) ? value : null;
};

const extractSensitivityType = (normalized) => (
  SENSITIVITY_PATTERNS.find((entry) => entry.pattern.test(normalized)) || null
);

export const extractPubgCalculatorSlots = (text = '', existingSlots = {}) => {
  const normalizedText = cleanToken(text);
  const slots = normalizePubgCalculatorSlotAliases(existingSlots);
  const invalidSlots = [];

  if (!slots.game) {
    if (CODM_PATTERN.test(normalizedText)) slots.game = 'codm';
    else if (FREE_FIRE_PATTERN.test(normalizedText)) slots.game = 'free_fire';
    else slots.game = 'pubg_mobile';
  }

  const weapon = extractWeapon(normalizedText);
  if (weapon && !slots.weapon) {
    slots.weapon = weapon.value;
    slots.weaponLabel = weapon.label;
  }

  const scope = extractScope(normalizedText);
  if (scope && !slots.baseScope) {
    slots.baseScope = scope.value;
    slots.baseScopeLabel = scope.label;
  }

  const invalidScope = extractInvalidScope(normalizedText, scope);
  if (invalidScope) invalidSlots.push(invalidScope);

  const baseValue = extractBaseValue(normalizedText);
  if (baseValue !== null && slots.baseValue === undefined) {
    slots.baseValue = baseValue;
  }

  const sensitivityType = extractSensitivityType(normalizedText);
  if (sensitivityType && !slots.sensitivityType) {
    slots.sensitivityType = sensitivityType.value;
    slots.controlMode = sensitivityType.controlMode;
  }

  return {
    skillId: 'pubg_mobile_calculator',
    slots,
    invalidSlots,
    unsupported: slots.game === 'codm' ? {
      game: 'codm',
      message: 'Calculadora automatica de CODM ainda nao esta disponivel. Posso te ajudar com Sensi-IA ou treino para CODM.'
    } : null
  };
};

export default extractPubgCalculatorSlots;
